// The dashboard, and the first thing anybody sees after signing in.
//
// It answers, in this order: how is it going, what am I posting next, what is
// already booked, and what have I got saved. The composer is on it rather than
// a click away because "write something" is the reason the product exists —
// and it is the *same* composer as /compose, not a copy of it, so a post
// started here behaves identically to one started there.
//
// Layout, on a desktop:
//
//   ┌──────── four numbers ────────────────────────────────┐
//   ├────────────────────────────┬─────────────────────────┤
//   │  quick compose             │  post preview           │
//   │                            ├─────────────────────────┤
//   │                            │  upcoming schedule      │
//   ├────────────────────────────┴─────────────────────────┤
//   │  recent performance                                  │
//   ├───────────────────────────┬──────────────────────────┤
//   │  recent posts             │  drafts                  │
//   └───────────────────────────┴──────────────────────────┘
//
// The upcoming schedule stays in the right-hand column at every desktop width.
// It is beside the composer and not below it because it is read *while*
// writing — "have I already got something going out that afternoon" is a
// question about the post being written, not a report.
//
// The one claim this screen makes in its own voice is the disclaimer at the
// bottom, and it is the important one: these are our delivery records, not the
// networks' analytics. We never asked Meta or Google for likes, views or reach,
// so nothing here may look as though it knows them.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { loadDashboard } from "../lib/api";
import { useWorkspace } from "../lib/WorkspaceContext";
import { logError } from "../lib/logger";
import { ComposerProvider, PostPreviewCard, QuickComposeCard } from "../compose";
import { CARDS } from "./cards";
import { DashboardHeader } from "./DashboardHeader";
import { DraftsCard } from "./DraftsCard";
import { PerformanceChartCard } from "./PerformanceChartCard";
import { RecentPostsCard } from "./RecentPostsCard";
import { StatCard, statsFrom } from "./StatCard";
import { useZone } from "../lib/userSettings";
import { UpcomingScheduleCard } from "./UpcomingScheduleCard";
import type { Dashboard as DashboardData } from "./types";

export function Dashboard() {
  const { active, can, ready } = useWorkspace();
  const workspaceId = active?.workspace_id ?? null;
  // Read once for the whole screen and handed to every card, so the KPI row,
  // the upcoming list and the destinations table cannot show three clocks.
  const zone = useZone();

  const [days, setDays] = useState(30);
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Reading the workspace's activity is its own permission. Someone without it
  // still gets the composer — it is the screen they land on now, and landing
  // on a page that only says "no" would be worse than the old behaviour.
  const mayRead = !ready || can("post.view");

  const load = useCallback(async () => {
    if (!workspaceId || !mayRead) return;
    setLoading(true);
    try {
      setData(await loadDashboard(workspaceId, days));
      setError(null);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "load_dashboard", workspaceId });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, days, mayRead]);

  useEffect(() => { void load(); }, [load]);

  const cards = data
    ? CARDS.filter((c) => (!c.permission || can(c.permission)) && (!c.when || c.when(data)))
    : [];

  return (
    <ComposerProvider>
      <DashboardHeader
        workspaceName={active?.name ?? null}
        days={days}
        onDays={setDays}
        busy={loading}
      />

      {error && (
        <div className="card err">
          Your activity could not be read. {error}
        </div>
      )}

      {/* Four numbers, or four cards the shape of four numbers. Never four
          blank rectangles and never nothing — the row must not change height
          when the answer arrives, or everything below it jumps. */}
      {mayRead && (
        <div className={loading && data ? "stat-cards stale" : "stat-cards"}>
          {data
            ? statsFrom(data, zone).map((stat) => <StatCard key={stat.id} stat={stat} />)
            : [0, 1, 2, 3].map((i) => (
                <section key={i} className="card stat-card stat-skeleton" aria-hidden="true">
                  <span className="stat-card-icon" />
                  <div className="stat-card-body">
                    <span className="skeleton-line wide" />
                    <span className="skeleton-line" />
                  </div>
                </section>
              ))}
        </div>
      )}

      {!mayRead && ready && (
        <div className="card muted">
          You do not have permission to see this workspace's activity, so the numbers and the
          schedule are not shown. You can still write and publish below.
        </div>
      )}

      <div className="dash-main">
        <QuickComposeCard />
        <div className="dash-side">
          <PostPreviewCard />
          {/* Right column, at every desktop width. */}
          {mayRead && data && <UpcomingScheduleCard data={data} />}
        </div>
      </div>

      {mayRead && data && (
        <>
          <PerformanceChartCard data={data} />

          <div className="dash-lower">
            <RecentPostsCard workspaceId={workspaceId} />
            <DraftsCard workspaceId={workspaceId} />
          </div>

          {/* Everything else the workspace can be asked about, from the card
              registry: where posts went, which accounts are healthy, what
              failed and why, how full the library is. Absent when there is
              nothing to say. */}
          {cards.length > 0 && (
            <div className={loading ? "dash-grid stale" : "dash-grid"}>
              {cards.map((card) => (
                <section key={card.id}
                         className={card.size === "full" ? "card dash-card full" : "card dash-card"}>
                  <div className="dash-card-head">
                    <h2>{card.title}</h2>
                    {card.lede && <p className="muted">{card.lede(data)}</p>}
                  </div>
                  {card.render(data, zone)}
                </section>
              ))}
            </div>
          )}

          {/* Not a footnote. A dashboard in this category is assumed to show
              engagement, and ours cannot: we never requested the scopes that
              carry it. Saying so is the difference between a limit and a lie.

              The second half is load-bearing since the destinations card
              landed. That card *does* show something the networks gave us — a
              Page's name and picture — so "all of this is ours" would no longer
              be true, and the honest line is narrower: what came from them is
              who the account is, never how it performed. */}
          <section className="card dash-card full dash-note">
            <h2>What this does not show</h2>
            <p className="muted">
              Likes, views, reach and follower counts are not here, and are not missing by
              accident. {" "}
              <Link to="/security">We never asked Facebook, Instagram or YouTube for
              them</Link> — the access this product holds is permission to publish, not
              permission to read your audience. The only thing above that came from a network
              is what an account <em>is</em>: its name, its picture, and the Page an Instagram
              account is linked to, read once when you connected it. Every number is our own
              record of what we sent and what happened to it.
            </p>
          </section>
        </>
      )}
    </ComposerProvider>
  );
}
